import React, { useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Divider,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Radio, Stack,
    styled,
    TextField,
    Typography,
} from '@mui/material';
import { ReactComponent as DownloadAppStoreBadgeLogo } from '../../assets/DownloadAppStoreBadge.svg';
import { ReactComponent as DownloadGooglePlayBadgeLogo } from '../../assets/DownloadGooglePlayBadge.svg';

const BorderedListItemButton = styled(ListItemButton)(({ theme }) => ({
    border: `1px solid rgba(0, 0, 0, 0.2)`,
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(1),
    '&:hover': {
        borderColor: `rgba(0, 0, 0, 0.4)`,
    },
}));

export default function TwoFactorSelectMethodStep({next, back, otpSecret, userEmail}: {next: CallableFunction, back: CallableFunction, otpSecret: string, userEmail: string}) {
    const [method, setMethod] = useState('EMAIL');
    const [target, setTarget] = useState(userEmail);

    const changeMethod = (newMethod: string) => {
        if (newMethod != method) {
            setTarget(newMethod === 'EMAIL' ? userEmail : '')
        }
        setMethod(newMethod)
    };

    const [showAlert, setShowAlert] = useState(false);
    const [alertText, setAlertText] = useState('');
    const __ = (window as any).__ ? (window as any).__ as (text: string) => string : (text: string) => text;

    const showError = (message: string) => {
        setShowAlert(true);
        setAlertText(message);
        setTimeout(() => {
            setShowAlert(false);
        }, 10000);
    }

    const validateAndGoNext = () => {
        if ('EMAIL' == method) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(target)) {
                showError(__('Niepoprawny adres e-mail'));
                return;
            }
        }

        Promise.resolve(next(method, target)).catch(() => {
            showError(__('Wybrany adres e-mail nie jest dostępny. Podaj inny adres.'));
        });
    }

    return <Box>
        { showAlert && <Alert sx={{ my: 2 }} severity="error">{alertText}</Alert> }
        <Typography variant="body2" gutterBottom>
            {__('Wybierz sposób weryfikacji:')}
        </Typography>
        <List component="nav">
            <BorderedListItemButton
                onClick={() => changeMethod('EMAIL')}
            >
                <ListItemIcon>
                    <Radio checked={method == 'EMAIL'} color="primary" />
                </ListItemIcon>
                    <ListItemText
                    primary={__('Kod na adres e-mail')}
                    secondary={
                        <React.Fragment>
                            {__('Wygodny dostęp przez Twoją skrzynkę pocztową.')}
                            { method == 'EMAIL' &&
                                    <TextField
                                    size="small"
                                    fullWidth
                                        label={__('Twój adres e-mail')}
                                    margin="normal"
                                    id="email-address"
                                    value={target}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => setTarget(e.target.value)}
                                />
                            }
                        </React.Fragment>
                    }
                />
            </BorderedListItemButton>
            <BorderedListItemButton
                onClick={() => {
                    changeMethod('APP')
                    setTarget(otpSecret)
                }}
            >
                <ListItemIcon>
                    <Radio checked={method == 'APP'} color="primary" />
                </ListItemIcon>
                <ListItemText
                    primary={__('Aplikacja mobilna (Zalecane)')}
                    secondary={
                        <React.Fragment>
                            {__('Najwyższy poziom ochrony. Wykorzystaj Google Authenticator, Microsoft Authenticator lub inną aplikację.')}
                            <Stack
                                direction="row"
                                spacing={1}
                                sx={{
                                    justifyContent: "flex-start",
                                    alignItems: "center",
                                    mt: 2,
                                }}
                            >
                                <a href="https://play.google.com/store/search?q=authenticator&c=apps" target="_blank" rel="noreferrer">
                                    <DownloadGooglePlayBadgeLogo height="40" width="120" />
                                </a>
                                <a href="https://www.apple.com/pl/search/authenticator?src=globalnav" target="_blank" rel="noreferrer">
                                    <DownloadAppStoreBadgeLogo height="40" width="120" />
                                </a>
                            </Stack>
                        </React.Fragment>
                    }
                />
            </BorderedListItemButton>
        </List>
        <Divider sx={{ my: 2 }} />
        <Box>
            <Stack spacing={2} direction="row">
                <Button onClick={() => validateAndGoNext()} variant="contained">{__('Włącz weryfikację dwuetapową (2FA)')}</Button>
                <Button onClick={() => back()} variant="outlined">
                    {__('Wstecz')}
                </Button>
            </Stack>
        </Box>
    </Box>;
}